import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import BrandLogo from "@/components/feature/BrandLogo";
import DemoModeBanner from "@/components/feature/DemoModeBanner";
import AdminLogin from "@/pages/admin/components/AdminLogin";
import { IS_OFFLINE } from "@/services/http";

type AdminRole = "admin" | "staff";

interface NavItem {
  to: string;
  label: string;
  icon: string;
  end?: boolean;
  /** Roles allowed to see this item; omitted = every console member. */
  roles?: AdminRole[];
}

interface NavGroup {
  label: string;
  /** Roles allowed to see this whole group; omitted = every console member. */
  roles?: AdminRole[];
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    label: "Overview",
    items: [
      { to: "/admin", label: "Dashboard", icon: "ri-dashboard-line", end: true },
      { to: "/admin/analytics", label: "Analytics", icon: "ri-line-chart-line" },
    ],
  },
  {
    label: "Catalog",
    items: [
      { to: "/admin/businesses", label: "Businesses", icon: "ri-store-2-line" },
      { to: "/admin/approvals", label: "Approvals", icon: "ri-checkbox-circle-line" },
      { to: "/admin/featured", label: "Featured", icon: "ri-vip-crown-line" },
      { to: "/admin/categories", label: "Categories", icon: "ri-apps-2-line" },
      { to: "/admin/subcategories", label: "Subcategories", icon: "ri-node-tree" },
      { to: "/admin/locations", label: "Locations", icon: "ri-map-pin-line" },
      { to: "/admin/tourist-spots", label: "Tourist Spots", icon: "ri-camera-lens-line" },
      { to: "/admin/media", label: "Media Library", icon: "ri-image-2-line" },
      { to: "/admin/plans", label: "Listing Plans", icon: "ri-price-tag-3-line" },
    ],
  },
  {
    label: "Community",
    items: [
      { to: "/admin/users", label: "Users", icon: "ri-group-line", roles: ["admin"] },
      { to: "/admin/reviews", label: "Reviews", icon: "ri-star-line" },
      { to: "/admin/leads", label: "Leads", icon: "ri-contacts-line" },
    ],
  },
  {
    label: "Monetization",
    roles: ["admin"],
    items: [
      { to: "/admin/advertisements", label: "Advertisements", icon: "ri-megaphone-line" },
      { to: "/admin/popup-ads", label: "Popup Ads", icon: "ri-window-2-line" },
      { to: "/admin/ad-placements", label: "Ad Placements", icon: "ri-layout-grid-line" },
      { to: "/admin/subscriptions", label: "Subscriptions", icon: "ri-repeat-line" },
    ],
  },
  {
    label: "CRM / ERP",
    roles: ["admin"],
    items: [
      { to: "/admin/crm/contacts", label: "Contacts", icon: "ri-contacts-book-2-line" },
      { to: "/admin/crm/orders", label: "Orders", icon: "ri-file-list-3-line" },
      { to: "/admin/crm/invoices", label: "Invoices", icon: "ri-bill-line" },
    ],
  },
  {
    label: "System",
    roles: ["admin"],
    items: [
      { to: "/admin/seo", label: "SEO Settings", icon: "ri-seo-line" },
      { to: "/admin/security", label: "Security", icon: "ri-shield-check-line" },
      { to: "/admin/activity", label: "Activity Log", icon: "ri-history-line" },
      { to: "/admin/announcements", label: "Marquee Messages", icon: "ri-notification-3-line" },
      { to: "/admin/settings", label: "System Settings", icon: "ri-settings-3-line" },
    ],
  },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const { settings } = useSiteSettings();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-50">
        <div className="flex flex-col items-center gap-3 text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
          <span className="text-sm">Loading admin console…</span>
        </div>
      </div>
    );
  }

  const role = user?.role as AdminRole | undefined;
  const isConsoleUser = role === "admin" || role === "staff";

  // The console doubles as its own sign-in gate. Anyone who is not an
  // administrator or staff member — signed out, or signed in as an owner —
  // sees the dedicated admin sign-in form here instead of the console.
  if (!user || !isConsoleUser) {
    return <AdminLogin currentRole={user?.role} currentName={user?.name} />;
  }

  // Hide admin-only groups/items from staff accounts.
  const visibleGroups = navGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !item.roles || (role && item.roles.includes(role))),
    }))
    .filter((group) => (!group.roles || (role && group.roles.includes(role))) && group.items.length > 0);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const sidebar = (
    <div className="flex h-full flex-col">
      <Link to="/" className="flex flex-col items-start gap-1 px-5 py-5">
        <BrandLogo className="h-11" />
        <span className="text-[11px] font-semibold uppercase tracking-wide text-foreground-500">Admin Console</span>
      </Link>

      <nav className="flex-1 space-y-4 overflow-y-auto px-3 pb-4">
        {visibleGroups.map((group) => (
          <div key={group.label}>
            <p className="px-3 pb-1.5 pt-2 text-[10px] font-bold uppercase tracking-wider text-foreground-400">
              {group.label}
            </p>
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
                      isActive ? "bg-primary-100 text-primary-800" : "text-foreground-700 hover:bg-background-100"
                    }`
                  }
                >
                  <i className={`${item.icon} text-lg`}></i>
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-background-200 p-3">
        <div className="flex items-center gap-3 rounded-md bg-background-100/70 p-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-foreground-950 text-xs font-bold text-primary-300">
            {user.name.slice(0, 2).toUpperCase()}
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground-950">{user.name}</p>
            <p className="truncate text-[11px] text-foreground-500">
              {role === "admin" ? "Administrator" : "Staff"}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="mt-2 flex w-full items-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium text-secondary-700 hover:bg-secondary-50 cursor-pointer"
        >
          <i className="ri-logout-box-r-line text-lg"></i> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-background-100/60">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-background-200 bg-background-50">
        {sidebar}
      </aside>

      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-72 max-w-[80%] bg-background-50 border-r border-background-200">{sidebar}</div>
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
            className="flex-1 bg-foreground-950/40 backdrop-blur-sm cursor-pointer"
          ></button>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <DemoModeBanner />
        <header className="sticky top-0 z-40 flex h-16 items-center gap-3 border-b border-background-200 bg-background-50/95 px-4 md:px-6 backdrop-blur">
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
            className="lg:hidden flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700 cursor-pointer"
          >
            <i className="ri-menu-line text-xl"></i>
          </button>
          {IS_OFFLINE && (
            <span className="hidden sm:inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1 text-[11px] font-bold text-secondary-900">
              <i className="ri-flask-line"></i> Preview environment · demo data
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 hover:border-primary-400 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-external-link-line"></i>
              <span className="hidden sm:inline">View site</span>
            </Link>
          </div>
        </header>

        <main className="flex-1 px-4 py-6 md:px-6 md:py-8">
          <div className="mx-auto max-w-[1300px]">
            <Outlet />
          </div>
        </main>
        <footer className="border-t border-background-200 px-4 py-4 text-center text-xs text-foreground-500 md:px-6">
          © {new Date().getFullYear()} {settings.siteName} — Admin Console
        </footer>
      </div>
    </div>
  );
}